<template>
  <div>
    <label v-if="label" class="d-block">{{ label }}</label>
    <validation-provider
      :name="name || label"
      :rules="rules"
      v-slot="{ errors }"
    >
      <v-select
        v-model="localValue"
        :options="formattedOptions"
        :filterable="false"
        @search="onSearch"
        :multiple="multiple"
        :disabled="disabled"
        :clearable="clearable"
        :reduce="reduce"
        label="label"
        append-to-body
        :calculate-position="withPopper"
      >
        <template v-if="$scopedSlots['no-options']" #no-options="slotProps">
          <slot name="no-options" v-bind="slotProps" />
        </template>
        <template v-if="$scopedSlots.option" #option="slotProps">
          <slot name="option" v-bind="slotProps" />
        </template>
        <template #list-footer>
          <li class="pagination">
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="!hasPrevPage"
              @click="prevPage"
            >
              <feather-icon icon="ChevronLeftIcon" size="18"/>
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="!hasNextPage"
              @click="nextPage"
            >
              <feather-icon icon="ChevronRightIcon" size="18"/>
            </b-button>
          </li>
        </template>
      </v-select>
      <p v-if="hint" class="mb-0 small">{{ hint }}</p>
      <p v-if="errors && errors.length" class="small text-danger">{{ errors[0] }}</p>
    </validation-provider>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted } from '@vue/composition-api'
import vSelect from 'vue-select'
import { BButton } from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import axios from '@axios'
import { debounce } from 'lodash'
import popperHelper from '@/libs/popper-helper'

export default defineComponent({
  name: 'CamerasProxySelect',
  components: {
    vSelect,
    BButton,
    ValidationProvider,
  },
  props: {
    value: {
      type: [Array, Object, String, Number],
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    rules: {
      type: [String, Object],
      default: '',
    },
    hint: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    pageSize: {
      type: Number,
      default: 8,
    },
    reduce: {
      type: Function,
      default: option => option,
    },
    selectedOptions: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const options = ref([])
    const search = ref('')
    const currentPage = ref(1)
    const limit = computed(() => props.pageSize)
    const meta = ref(null)
    const loading = ref(false)
    const resolvedSelectedOptions = ref([])
    let selectedOptionsRequestId = 0

    const localValue = computed({
      get: () => props.value,
      set: (val) => emit('input', val),
    })

    const formattedOptions = computed(() => {
      const optionsByValue = new Map()
      const availableOptions = [
        ...props.selectedOptions,
        ...resolvedSelectedOptions.value,
        ...options.value,
      ]

      availableOptions.forEach((item) => {
        const original = item.original || item
        const value = item.value || original.id

        if (!value) return

        optionsByValue.set(value, {
          value,
          label: item.label || original.camera?.name || original.camera?.camera_name || `Camera (${value})`,
          original,
        })
      })

      return [...optionsByValue.values()].sort((a, b) => a.label.localeCompare(b.label))
    })

    const hasNextPage = computed(() => {
      if (meta.value) {
        return meta.value.current_page < meta.value.last_page
      }
      return false
    })

    const hasPrevPage = computed(() => {
      if (meta.value) {
        return meta.value.current_page > 1
      }
      return false
    })

    const fetchData = async () => {
      loading.value = true
      try {
        const { data } = await axios.get('/v1/proxy_camera?camerasOnly=true', {
          params: {
            q: search.value,
            max: limit.value,
            "page[n]": currentPage.value,
            sortBy: "id",
          },
        })
        meta.value = data.meta
        options.value = data.data
        emit('options-loaded', data.data)
      } catch (error) {
        console.error("CamerasProxySelect: request error", error)
        emit('error', error)
      } finally {
        loading.value = false
      }
    }

    const getSelectedIds = (value) => {
      const values = Array.isArray(value) ? value : [value]

      return values
        .map(item => (typeof item === 'object' ? item?.value ?? item?.id : item))
        .filter(Boolean)
    }

    /** Resolve selected cameras that are outside the current paginated result. */
    const loadSelectedOptions = async (value) => {
      const requestId = ++selectedOptionsRequestId
      const selectedIds = getSelectedIds(value)
      const availableOptions = [
        ...props.selectedOptions,
        ...resolvedSelectedOptions.value,
        ...options.value,
      ]
      const optionsByValue = new Map(
        availableOptions.map(item => {
          const original = item.original || item

          return [item.value ?? original.id, item]
        })
      )
      const missingIds = selectedIds.filter(id => !optionsByValue.has(id))

      if (missingIds.length) {
        const responses = await Promise.allSettled(
          missingIds.map(id => axios.get(`/v1/proxy_camera/${id}`))
        )

        responses.forEach(response => {
          const camera = response.status === 'fulfilled' ? response.value.data?.data : null

          if (camera) {
            optionsByValue.set(camera.id, camera)
          }
        })
      }

      if (requestId !== selectedOptionsRequestId) return

      resolvedSelectedOptions.value = selectedIds
        .map(id => optionsByValue.get(id))
        .filter(Boolean)
    }

    const onSearch = debounce(async (query, loadingFn) => {
      if (loadingFn) loadingFn(true)
      search.value = query || ''
      currentPage.value = 1
      await fetchData()
      if (loadingFn) loadingFn(false)
    }, 500)

    const nextPage = () => {
      if (hasNextPage.value) {
        currentPage.value += 1
      }
    }

    const prevPage = () => {
      if (hasPrevPage.value) {
        currentPage.value -= 1
      }
    }

    watch(currentPage, () => {
      fetchData()
    })

    watch(
      () => props.value,
      value => {
        loadSelectedOptions(value)
      },
      { immediate: true, deep: true }
    )

    onMounted(() => {
      fetchData()
    })

    return {
      localValue,
      options,
      formattedOptions,
      hasNextPage,
      hasPrevPage,
      onSearch,
      nextPage,
      prevPage,
      withPopper: popperHelper.withPopper,
    }
  },
})
</script>

<style lang="scss" scoped>
.pagination {
  display: flex;
  justify-content: space-around;
  margin: 0.25rem 0.25rem 0;
}

.pagination button {
  flex-grow: 1;

  &:first-child {
    margin-right: 5px;
  }
}

.pagination button:hover {
  cursor: pointer;
}
</style>
